/* Query and mutation hooks for People — STORY-015.
 *
 * Server state lives in TanStack Query and nowhere else. The search is part of
 * the key, so two searches are two cached pages rather than one that flickers
 * between them, and every write invalidates the PREFIX so a correction refreshes
 * whichever page is on screen without any caller knowing what it was filtered by.
 */

import {
  type CreatePersonRequest,
  type ListPeopleQuery,
  peopleResponseSchema,
  personDetailSchema,
  personErasureResponseSchema,
  personExportSchema,
  type UpdatePersonRequest,
} from '@eventuley/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../../shared/api/client.js';
import { usageKey } from '../../plans/index.js';

export const peopleKey = (organisationId: string, query?: ListPeopleQuery) =>
  query === undefined
    ? (['organisations', organisationId, 'people'] as const)
    : (['organisations', organisationId, 'people', query] as const);

const toSearchParams = (query: ListPeopleQuery): string => {
  const params = new URLSearchParams();
  if (query.search) params.set('search', query.search);
  if (query.sort) params.set('sort', query.sort);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  const rendered = params.toString();
  return rendered === '' ? '' : `?${rendered}`;
};

export function usePeople(organisationId: string, query: ListPeopleQuery = {}) {
  return useQuery({
    queryKey: peopleKey(organisationId, query),
    queryFn: () =>
      request(`/organisations/${organisationId}/people${toSearchParams(query)}`, {
        schema: peopleResponseSchema,
      }),
    /* The previous page stays on screen while the next loads, so typing in the
     * search box does not blank the list on every keystroke. */
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/**
 * One Person, opened — the only read that carries their note.
 *
 * Enabled only while the editor is open, which is the point rather than an
 * optimisation: the list does not ship Article 9 free text to a picker that
 * renders names, so the note is fetched at the moment there is a reader for
 * it and not before. ADR-0017.
 */
export function usePerson(organisationId: string, personId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...peopleKey(organisationId), personId] as const,
    queryFn: () =>
      request(`/organisations/${organisationId}/people/${personId}`, {
        schema: personDetailSchema,
      }),
    enabled,
    retry: false,
  });
}

/**
 * Every write refreshes the usage figures too.
 *
 * The contact cap is the reason. A workspace one contact from its limit needs
 * the plans screen to say so the moment it gets there, and a cached count that
 * lags behind the book turns the refusal into a surprise — STORY-031's whole
 * argument is that a limit nobody saw coming is experienced as a bug.
 */
const refresh = (queryClient: ReturnType<typeof useQueryClient>, organisationId: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: peopleKey(organisationId) }),
    queryClient.invalidateQueries({ queryKey: usageKey(organisationId) }),
    /* Guest lists name the Person by reference and resolve the name on every
     * read, so a correction — or an erasure — has to reach them too. Without
     * this a renamed or erased contact goes on showing the old name on every
     * list they are on until something else happens to refetch. */
    queryClient.invalidateQueries({
      queryKey: ['organisations', organisationId, 'events'] as const,
    }),
  ]);

export function useCreatePerson(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreatePersonRequest) =>
      request(`/organisations/${organisationId}/people`, {
        method: 'POST',
        schema: personDetailSchema,
        body: values,
      }),
    onSuccess: async () => {
      await refresh(queryClient, organisationId);
    },
  });
}

export function useUpdatePerson(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ personId, ...values }: UpdatePersonRequest & { personId: string }) =>
      request(`/organisations/${organisationId}/people/${personId}`, {
        method: 'PATCH',
        schema: personDetailSchema,
        body: values,
      }),
    onSuccess: async () => {
      await refresh(queryClient, organisationId);
    },
  });
}

export function useDeletePerson(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (personId: string) =>
      request(`/organisations/${organisationId}/people/${personId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await refresh(queryClient, organisationId);
    },
  });
}

/**
 * Everything held about one Person — STORY-019.
 *
 * A mutation rather than a query, and deliberately: an export is an action
 * somebody takes, not state a screen holds. Answering a subject access request
 * should not mean the answer sits in a cache afterwards, being refetched on
 * every focus change — this is the most sensitive payload the product
 * produces.
 */
export function useExportPerson(organisationId: string) {
  return useMutation({
    mutationFn: (personId: string) =>
      request(`/organisations/${organisationId}/people/${personId}/export`, {
        schema: personExportSchema,
      }),
  });
}

/**
 * Erases a Person — STORY-019, ADR-0015.
 *
 * Irreversible. Deletes when nothing depends on the record and anonymises in
 * place when something does, so every Event headcount is unchanged.
 */
export function useErasePerson(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (personId: string) =>
      request(`/organisations/${organisationId}/people/${personId}/erasure`, {
        method: 'POST',
        schema: personErasureResponseSchema,
      }),
    /* Guest lists too, not only the contact book: an anonymised Person is
     * named by reference on every list they are on, so a stale cache would go
     * on showing a name that no longer exists anywhere. */
    onSuccess: () => refresh(queryClient, organisationId),
  });
}
